import { useTranslation } from 'react-i18next';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@munifocus/ui';
import { LEGACY_BYLAW_CATEGORIES, type BylawCategory } from '@munifocus/contracts-gov';
import { pickLang, useLocalGovernmentWorkflows } from '../../lib/bylaw-workflows-api';
import type { FileForm } from './useFileForm';

/**
 * Which by-law type the file is opened under — and so which workflow it runs.
 *
 * The choice depends on the file's local government: the original categories
 * are always on offer, the configured ones (property standards, noise, …) only
 * where the municipality runs them. Until a municipality is chosen, or when
 * its workflows cannot be read, the original three are offered exactly as
 * before — the module changes nothing anywhere it is not switched on.
 */
export function BylawCategoryPicker({ form }: { form: FileForm }) {
  const { t, i18n } = useTranslation();
  const { values, set } = form;
  const workflows = useLocalGovernmentWorkflows(values.localGovernmentId || null);

  const available = workflows.data?.available ?? [];
  const offered: BylawCategory[] = available.length
    ? available.map((d) => d.category)
    : [...LEGACY_BYLAW_CATEGORIES];
  // A file already carrying a category keeps it on offer, even where it is no
  // longer enabled — editing must never silently change the file's workflow.
  if (values.bylawCategory && !offered.includes(values.bylawCategory)) {
    offered.push(values.bylawCategory);
  }
  const selected = available.find((d) => d.category === values.bylawCategory);
  const summary =
    selected?.engine === 'configured' ? pickLang(selected.summary, i18n.language) : '';

  return (
    <FormControl fullWidth size="small">
      <InputLabel>{t('gov.file.bylawCategory')}</InputLabel>
      <Select
        label={t('gov.file.bylawCategory')}
        aria-label={t('gov.file.bylawCategory')}
        value={values.bylawCategory}
        onChange={(e) => set('bylawCategory', e.target.value as BylawCategory)}
      >
        {offered.map((category) => (
          <MenuItem key={category} value={category}>
            {t(`gov.bylaw.${category}`)}
          </MenuItem>
        ))}
      </Select>
      {summary && <FormHelperText>{summary}</FormHelperText>}
    </FormControl>
  );
}
